/**
 * Board layout — the solver seam. Owns everything that turns the loaded
 * model into a renderable board `root`: the layout inputs (flattened modules
 * + effective edges with the aspect overlay folded in), the global edge/port
 * aggregation (`globalCtx`), the cached `root` build, and the drag / animation
 * machinery that drives positions.
 *
 * This is ONE hook on purpose. The idle (lazy engine) path, the drag path, and
 * the animation path all share the same mutable refs — `previousLayoutRef`,
 * `engineRef`, `lastRootRef`, `lastEagerRef`, `animRootPositionsRef`,
 * `animGateRef`. The animation's end state is required to EQUAL what a sync
 * solve produces (the anim-sync equality invariant); splitting these refs
 * across two hooks is exactly how that equality silently breaks. One hook, one
 * owner of all the refs.
 *
 * Placement seam: `usePlacements` keeps only the data layer. The drag mechanics
 * (settle ticker, `isDragging`, warm-start reset) live here and are exposed as
 * `onPlaced()` / `resetLayout()` so App composes them with the placements
 * hook's `place` / `reset`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ArchitectureModel,
  DependencyEdge,
  Module,
  ViewProjectionPlan,
} from "@archlang/scene/arch-model";
import { moduleKey, applyProjection, extractWidgetSpec } from "@archlang/scene/arch-model";
import { resolveNodeSize, NODE_W, NODE_H } from "@archlang/scene/board";
import { paletteColor } from "@archlang/scene";
import { boundaryPortsForMark } from "../board/ports.js";
import {
  prepareRootStressInput,
  stressLayoutSteps,
  type BoardSpace,
  type PlacementOverrides,
  type PreviousLayout,
} from "../board";
import {
  asSource,
  createBoardEngine,
  diffModel,
  edgesSignature,
  engineSource,
  IncrementalFlatten,
  IncrementalLayoutStructure,
  installWindowDebug,
  prepareLayout,
  shouldDropWarmStart,
  type BoardEngine,
  type ExternalPort,
  type LayoutContext,
  type LayoutStructure,
  type ModelInput,
  type SpaceSource,
} from "@archlang/scene/board";
import { applyAspectParents, type AspectOverlay } from "../aspect-overlay";

// How many nested preview levels below the focal the lazy engine solves per
// request. The renderer reveals nested previews until they shrink past
// INNER_RENDER_SCALE (or auto-rebases into them), which in practice is ~2–3
// levels at a usable zoom — so solving 3 deep covers what's visible while the
// rest stays lazy until the user dives.
const RENDER_DEPTH = 3;

/** Drag relaxation throttle — ON by default (the dragged node tracks its
 *  pin at full rate regardless; only neighbour relaxation halves, and the
 *  release solve makes the final layout identical). Opt-out for A/B feel
 *  checks: `localStorage["arch.layout.dragThrottle"] = "0"`. */
function readDragThrottleSwitch(): boolean {
  try {
    return typeof localStorage === "undefined" || localStorage.getItem("arch.layout.dragThrottle") !== "0";
  } catch {
    return true;
  }
}

/**
 * Build a cache key that captures everything `buildBoardFromModules`
 * actually consumes. Used to short-circuit re-solves when an SSE
 * update produces a structurally identical model — the stress solver
 * is deterministic in principle but accumulates sub-pixel drift on
 * each warm-start re-solve, which made modules visibly jiggle on
 * every keystroke. Reusing the previous root verbatim freezes them.
 */
function computeLayoutInputKey(
  flat: ReadonlyArray<Module & { parentId?: string }>,
  edges: readonly DependencyEdge[],
  placements: PlacementOverrides,
  isDragging: boolean,
  animating: boolean,
  solveGen: number,
  /** View overlays BAKED into the built root but invisible to the module/
   *  edge content above — two views can project content-identical inputs
   *  yet differ in stubs (`show Web` vs `show Web; hide * <> outside`). */
  boundaryPorts?: ReadonlyMap<string, readonly ExternalPort[]>,
  /** Same overlay class: two grouped views can project identical flat
   *  content and differ only in frame assignment (`group by a` vs
   *  `group by b` over the same member set, or a nesting-only change). */
  groupFrames?: readonly {
    id: string;
    label: string;
    memberBoardIds: readonly string[];
    parentId?: string;
  }[],
): string {
  const mods = flat
    .map(
      (m) =>
        // Include layout/render-affecting CONTENT (not just structure): a
        // description / field / label / interface edit must bust this key so
        // the board rebuilds. The engine then re-solves only the changed
        // space (content folds into its per-space signature too); skipping the
        // rebuild here would leave the card showing stale text.
        `${m.id ?? m.name}:${m.parentId ?? ""}:${m.typeName}:` +
        JSON.stringify([m.description ?? null, m.fields, m.aspects, m.interfaces, m.surfaces]),
    )
    .sort()
    .join("|");
  const eds = edges
    .map(
      (e) =>
        `${e.fromModuleId ?? e.fromModulePath.join(".")}>` +
        `${e.toModuleId ?? e.toModulePath.join(".")}.${e.toInterfacePath.join(".")}`,
    )
    .sort()
    .join("|");
  const plc = JSON.stringify(
    Array.from(placements.entries()).sort(([a], [b]) => (a < b ? -1 : 1)),
  );
  const bp = boundaryPorts
    ? [...boundaryPorts.entries()]
        .map(([id, ps]) =>
          `${id}=${ps
            .map(
              (p) =>
                `${p.direction}${p.peerId}x${p.details.length}` +
                `~${p.widgetStyle?.color ?? ""}~${p.widgetStyle?.width ?? ""}`,
            )
            .sort()
            .join(",")}`,
        )
        .sort()
        .join("|")
    : "";
  const gf = groupFrames
    ? groupFrames
        .map((f) => `${f.id}<${f.parentId ?? ""}:${f.label}:${[...f.memberBoardIds].sort().join(",")}`)
        .join("|")
    : "";
  return `${mods}::${eds}::${plc}::bp=${bp}::gf=${gf}::d=${isDragging ? 1 : 0}::a=${animating ? 1 : 0}::g=${solveGen}`;
}

export interface BoardLayoutController {
  /** The renderable board root — lazy SpaceSource (idle) or eager tree
   *  (drag / animation). Null until a model loads. */
  readonly root: BoardSpace | SpaceSource | null;
  /** Global edge/port aggregation for the whole model — feeds the connection
   *  index and the selected-edge lookup (both stay in App). */
  readonly globalCtx: LayoutContext | null;
  /** Resolve a module id to its containing space's focal path (for flyTo). */
  readonly moduleFocalPath: (moduleId: string) => readonly string[] | null;
  /** Ghost-repair mover id during the current animation step (board highlight). */
  readonly ghostMoverId: string | null;
  readonly animating: boolean;
  readonly animPaused: boolean;
  /** Discard warm-start and re-solve from scratch (Recompute button). */
  readonly recomputeLayout: () => void;
  /** Animate the relaxation from cold seed to equilibrium (Animate button). */
  readonly animateRecompute: () => Promise<void>;
  readonly toggleAnimPause: () => void;
  readonly animStep: () => void;
  /** Placement seam: kick the settle tail after a hand-placement. */
  readonly onPlaced: () => void;
  /** Placement seam: drop the warm-start cache + exit drag for a cold solve. */
  readonly resetLayout: () => void;
  /** The ACTIVE view's projection plan the current layout inputs were built
   *  under (S15) — null on the identity board. The style-output consumers
   *  (view coloring, focus emphasis) read it from here so they can never drift
   *  from what the board actually projected. */
  readonly viewPlan: ViewProjectionPlan | null;
  /** Under an active plan: origin qualified key → the board node id(s)
   *  rendering it (clone id / projected key / copy ids — see the scene
   *  `ProjectionResult.nodeIdsByOrigin`). Null on the identity board. */
  readonly viewNodeIds: ReadonlyMap<string, readonly string[]> | null;
}

/** An undocumented drift pair — both endpoints as MODEL module keys. Mirrors
 *  telemetry's `DriftPair` (kept structural here to avoid a hooks→telemetry
 *  import). */
export interface UndocumentedPair {
  readonly from: string;
  readonly to: string;
}

/** Stable empty default — a fresh `[]` per render would bust the layoutInputs memo. */
const EMPTY_TOPOLOGY: readonly UndocumentedPair[] = [];

/**
 * Fold the live drift topology into the edge set as first-class `violation`
 * edges, so an observed-but-unmodeled cross-space call routes + auto-derives its
 * stub port through the normal board build (see scene `buildLayoutStructure`).
 * Filtered to endpoints present in the projected module set — a drift edge to a
 * view-filtered module can't be drawn. Returns `effEdges` BY REFERENCE when the
 * topology is empty, so the non-telemetry board stays byte-identical.
 */
function foldViolationEdges(
  effEdges: readonly DependencyEdge[],
  flat: ReadonlyArray<Module & { parentId?: string }>,
  topology: readonly UndocumentedPair[],
): readonly DependencyEdge[] {
  if (topology.length === 0) return effEdges;
  const present = new Set(flat.map((m) => moduleKey(m)));
  const synthetic: DependencyEdge[] = [];
  for (const { from, to } of topology) {
    if (from === to || !present.has(from) || !present.has(to)) continue;
    synthetic.push({
      fromModulePath: [],
      fromModuleId: from,
      toModulePath: [],
      toModuleId: to,
      toInterfacePath: [],
      source: "process",
      violation: true,
    });
  }
  return synthetic.length ? [...effEdges, ...synthetic] : effEdges;
}

export function useBoardLayout({
  model,
  edges,
  aspectOverlay,
  placements,
  structural = true,
  viewPlan = null,
  undocumentedTopology = EMPTY_TOPOLOGY,
}: {
  model: ArchitectureModel | null;
  edges: readonly DependencyEdge[];
  aspectOverlay: AspectOverlay;
  placements: PlacementOverrides;
  /** Live-conformance drift topology (observed-but-unmodeled pairs, resolved to
   *  module keys). Folded into the board build as `violation` edges. Keyed on
   *  the SET (not metrics) so re-layout fires only when drift shape changes;
   *  empty (the default) → the board is byte-identical to today. */
  undocumentedTopology?: readonly UndocumentedPair[];
  /** The active view's projection plan (S15). `null` (no view active) → the board
   *  renders the full model unchanged: `applyProjection(mods, edges, null)` returns
   *  its inputs by reference, so `layoutInputs` + the content cache key are
   *  byte-identical to the un-projected board (the Phase-0 identity invariant). A
   *  non-null plan filters/re-parents/groups/lifts + styles the projected set. */
  viewPlan?: ViewProjectionPlan | null;
  /** From the incremental compiler: did this edit change the symbol surface?
   *  A non-structural (pure value) edit lets the board skip cross-level cache
   *  invalidation — its per-space content signature catches the change. Defaults
   *  to `true` (always invalidate) so callers that don't pass it stay correct. */
  structural?: boolean;
}): BoardLayoutController {
  // True while the user's pointer is actively dragging a node. Drives the
  // solver's "drag" budget so neighbours drift gently instead of snapping.
  const [isDragging, setIsDragging] = useState(false);

  // A MODEL EDIT ends the drag regime. `isDragging` deliberately stays on
  // after a placement — but an edit
  // arriving afterwards is a new gesture of its own and must run the idle
  // edit-solve path: under sticky drag mode it would run on the 2-iter
  // drag budget with the edit baseline skipped, deadening every response
  // until the next explicit reset. Placements aren't in the dep list, so
  // an active pointer drag never trips this; an edit landing mid-gesture
  // (live recompile) yields one idle solve — pins hold it — and the next
  // placement re-enters drag mode.
  useEffect(() => {
    setIsDragging(false);
  }, [model, edges]);

  // Stress-layout warm-start: carry each solve's output into the next run so
  // adding/removing a module nudges neighbours rather than reshuffling the
  // world. Cleared on "Reset layout" or "Recompute".
  const previousLayoutRef = useRef<PreviousLayout>(new Map());
  /** Edge count the current warm-start layout was solved with — a material
   *  jump (model still streaming in, bulk edit) invalidates the warm start
   *  (see shouldDropWarmStart). */
  const warmEdgeCountRef = useRef<number | null>(null);
  /** Layout snapshot at drag-gesture start — the solver's trim baseline:
   *  standing forces are absorbed against it, gesture deltas act in full. */
  const dragBaseRef = useRef<PreviousLayout | null>(null);
  /** Structure the CURRENT `previousLayoutRef` rects were solved under —
   *  updated together with them in the snapshot effect, so the pair is
   *  always internally consistent (rects from render N, structure from
   *  render N — never a mix). */
  const solvedStructureRef = useRef<LayoutStructure | null>(null);
  /** Content identity (the `layoutInputs` object) the edit baseline below
   *  was latched for. */
  const editContentRef = useRef<unknown>(null);
  /** Pending model-edit baseline — latched on content change, consumed by
   *  the first idle solve of that content (see the memo). `groupFrames` is
   *  the PRE-edit frame membership (frame groups): the calibration world's
   *  forced partition, and the term that makes a membership flip register
   *  as an edit at all (it is geometry-invisible otherwise). */
  const editBaseRef = useRef<{
    rects: PreviousLayout;
    structure: LayoutStructure;
    groupFrames?: readonly {
      id: string;
      label: string;
      memberBoardIds: readonly string[];
      parentId?: string;
    }[];
  } | null>(null);
  /** The view plan the current warm start belongs to (+ whether it grouped).
   *  A view-plan change where EITHER side has `group by` frames drops the
   *  root warm start: flat emit keeps many node ids stable across the
   *  toggle while their coordinate meaning changes, and a stale warm rect
   *  would seed wrong geometry that `chooseSolveParams` then locks in as an
   *  incremental solve. Non-grouped transitions keep the warm world — the
   *  carried-verbatim guarantee of the view-transition gate. */
  const warmViewPlanRef = useRef<{ plan: unknown; hadFrames: boolean }>({
    plan: null,
    hadFrames: false,
  });
  /** The non-grouped warm world, stashed while a grouped view is active —
   *  restored on exit so leaving a grouped view never reshuffles the board
   *  the user left behind. */
  const framelessWarmStashRef = useRef<PreviousLayout | null>(null);
  /** Drag relaxation throttle (default ON; opt-out
   *  `localStorage["arch.layout.dragThrottle"]="0"`): alternates per drag
   *  frame; on skip frames the solver is bypassed entirely (the dragged
   *  node still tracks its pin), halving big-board drag cost. The parity
   *  advances only when the PLACEMENTS IDENTITY changes (one real drag
   *  frame), so a double-invoked memo factory (StrictMode) or an unrelated
   *  re-render can't flip it; it resets between drags (first frame of every
   *  drag = solve frame). */
  const dragFrameParityRef = useRef(0);
  const dragParityPlacementsRef = useRef<unknown>(null);
  /** True when a drag/animation render already solved (and engine-cached)
   *  the pending baseline's content — the consuming idle solve must then
   *  invalidate, or the engine's signature (blind to editBase) hands back
   *  the drag-cached spaces and the edit response never runs. */
  const editStaleRef = useRef(false);
  // Generation counter — bump to force a fresh solve even when nothing else
  // changed. Used by the "Recompute" / "Animate" buttons so the user can
  // see what the solver would do from cold without touching placements.
  const [solveGen, setSolveGen] = useState(0);

  // Animation mode state. While `animating` is true, every frame bumps
  // `solveGen` and the useMemo below calls the solver with a small chunk
  // budget and the current progress value. The rAF loop advances progress
  // 0→1 across ANIMATE_TOTAL_FRAMES, warm-starting each chunk from the
  // previous one's output.
  const [animating, setAnimating] = useState(false);
  const [animPaused, setAnimPaused] = useState(false);
  // Current root-space positions published by the animation generator.
  // When non-null, buildBoardFromModules uses these verbatim for root
  // instead of calling stressLayout. Nested spaces still solve normally.
  const animRootPositionsRef = useRef<ReadonlyMap<string, { x: number; y: number; w: number; h: number }> | null>(null);
  // Ghost-repair mover id during the current animation step. Mirrored to
  // state so React re-renders the board with the new highlight; the ref
  // avoids redundant setState when the id is unchanged across frames.
  const ghostMoverIdRef = useRef<string | null>(null);
  const [ghostMoverId, setGhostMoverId] = useState<string | null>(null);
  const animAbortRef = useRef(0); // incremented to abort any in-flight loop
  // Async gate used to pause the animation loop between frames. `wait()`
  // resolves immediately when the gate is open (playing) or never
  // resolves while closed (paused). Step lets exactly one pending waiter
  // through without re-opening — so the next frame renders, then blocks
  // again. Built from scratch each animation run.
  interface AnimGate {
    wait: () => Promise<void>;
    pause: () => void;
    resume: () => void;
    step: () => void;
  }
  const animGateRef = useRef<AnimGate | null>(null);

  // Cache the last solved root so structure-identical re-parses can
  // reuse it verbatim. The stress solver is mostly deterministic but
  // accumulates small float drift between runs even with warm-start
  // — letting that drift through on every SSE-driven /api/files
  // refetch made modules jiggle on every keystroke. Compare on a
  // semantic key (modules, edges, placements, drag/anim state) and
  // hand back the same root reference when nothing relevant changed.
  const lastRootRef = useRef<{
    key: string;
    root: BoardSpace | SpaceSource;
  } | null>(null);
  // Persistent lazy engine — the single board path (idle, drag, animation). It
  // caches per-space solves so an edit re-solves only the spaces `diffModel`
  // reports dirty, and a drag re-solves only the perturbed space; every other
  // space is carried verbatim from the prior frame's engine.
  const engineRef = useRef<BoardEngine | null>(null);
  const prevModelInputRef = useRef<ModelInput | null>(null);
  // Prior edit's topology + raw-edge signature, kept so the next edit's
  // `diffModel` reuses the built `LayoutStructure` (no rebuild) and can skip the
  // diff entirely when no edge changed (see the idle branch).
  const prevStructureRef = useRef<LayoutStructure | null>(null);
  const prevEdgeSigRef = useRef<string | null>(null);
  // Persistent incremental flatten + structure. The serializer keeps unchanged
  // modules' refs stable; `IncrementalFlatten` carries that to the flattened
  // clones, and `IncrementalLayoutStructure` patches the topology from a module/
  // edge identity diff — so a single-module edit costs O(changed-subtree), not a
  // full O(modules+edges) rebuild.
  const flattenRef = useRef<IncrementalFlatten | null>(null);
  const structureBuilderRef = useRef<IncrementalLayoutStructure | null>(null);
  if (!flattenRef.current) flattenRef.current = new IncrementalFlatten();
  if (!structureBuilderRef.current) structureBuilderRef.current = new IncrementalLayoutStructure();
  // Flattened module list + effective edges (with any aspect overlay folded
  // in). Shared by the layout build AND the connection index — the index is
  // position-independent (edges/ports only), so it derives from these inputs
  // via `prepareLayout`'s global aggregation rather than walking a solved tree.
  const layoutInputs = useMemo(() => {
    if (!model) return null;
    // Projection FIRST (MAJOR-6), then the aspect overlay onto the projected set —
    // the web reflects what the view shows, not the full model. `viewPlan===null`
    // (default) skips applyProjection entirely, so the default board's modules/edges
    // are the exact same references as today (byte-identical; Phase-0 invariant).
    const projected = viewPlan ? applyProjection(model.modules, edges, viewPlan) : null;
    const projModules = projected ? projected.modules : model.modules;
    const projEdges = projected ? projected.edges : edges;
    const hasOverlay = aspectOverlay.modules.length > 0 || aspectOverlay.edges.length > 0;
    const flattened = flattenRef.current!.flatten(
      hasOverlay ? [...projModules, ...aspectOverlay.modules] : projModules
    );
    // An aspect place renders inside the subspace its members live in
    // (parentByNodeId — hub at the members' deepest common ancestor,
    // replicated copies beside their member). Applied only when the overlay
    // is active, so the plain board's flat list keeps its identity.
    const flat =
      hasOverlay && aspectOverlay.parentByNodeId.size > 0
        ? applyAspectParents(flattened, aspectOverlay.parentByNodeId)
        : flattened;
    const modeledEdges = hasOverlay ? [...projEdges, ...aspectOverlay.edges] : projEdges;
    // Live-conformance fold: drift edges become first-class `violation` edges so
    // they route + auto-derive stub ports natively. By reference when empty →
    // the non-telemetry board is byte-identical (Phase-0 invariant preserved).
    const effEdges = foldViolationEdges(modeledEdges, flat, undocumentedTopology);
    // The origin→board-id bridge (S15 style outputs): the plan's colorResult/
    // legend/pins/focus are keyed on ORIGIN qualified names; this maps them to
    // the id-keyed board seams. Null on the identity board.
    const viewIndex = projected?.nodeIdsByOrigin ?? null;
    // `group by` frames (frame-groups): board-id member lists — feeds the
    // solver's forced partition + the rendered BoardSpace.groupFrames.
    const groupFrames = projected?.groupFrames;
    // View boundary stubs (VIEWS §7): plan marks are keyed on ORIGIN
    // qualified names — bridge to board ids (every copy of a copied origin
    // shows its own stubs) and synthesize the port shape PortsLayer renders
    // (details.length drives the ×N count badge).
    let boundaryPorts: Map<string, ExternalPort[]> | undefined;
    if (projected?.boundary?.length) {
      boundaryPorts = new Map();
      for (const mark of projected.boundary) {
        const ids = projected.nodeIdsByOrigin?.get(mark.nodeKey) ?? [mark.nodeKey];
        for (const boardId of ids) {
          const stubs = boundaryPortsForMark(mark, boardId, paletteColor);
          const prev = boundaryPorts.get(boardId);
          boundaryPorts.set(boardId, prev ? [...prev, ...stubs] : stubs);
        }
      }
    }
    return {
      flat,
      effEdges,
      viewIndex,
      ...(groupFrames ? { groupFrames } : {}),
      ...(boundaryPorts ? { boundaryPorts } : {}),
    };
  }, [model, edges, aspectOverlay, viewPlan, undocumentedTopology]);

  // View `pin <x>,<y>` directives (S15/S14) fold into the SAME placements
  // channel drags write — pinned as hard anchors the solver relaxes around.
  // Each pin is sized by the exact `resolveNodeSize` the solver uses (a pinned
  // rect's w/h double as the node's effective solver size — a wrong size would
  // distort it). A USER placement for the same node wins: it is the later
  // gesture. Identity board (no plan / no pins) returns `placements` BY
  // REFERENCE — the default path stays byte-identical.
  const effectivePlacements = useMemo<PlacementOverrides>(() => {
    const pins = viewPlan?.pins;
    if (!pins || pins.size === 0 || !layoutInputs) return placements;
    const byId = new Map(layoutInputs.flat.map((m) => [moduleKey(m), m] as const));
    const merged = new Map<string, { x: number; y: number; w: number; h: number }>();
    for (const [qual, pos] of pins) {
      // A `group by` fan-out renders a pinned origin as SEVERAL copies (one
      // per group container). The placements channel is per-node and space-
      // LOCAL (each space solve reads only its own children's rects — see
      // scene layout.ts pinned-map build), so replaying the ONE origin-frame
      // coordinate into every container would anchor each copy at the same
      // arbitrary local offset — multiplying the distortion by the group
      // count. Pin only the FIRST rendered id (deterministic: the plan bridge
      // lists ids in the projection's root-order DFS); the remaining copies
      // keep solver freedom inside their containers.
      const ids = layoutInputs.viewIndex?.get(qual) ?? [qual];
      const id = ids.find((candidate) => byId.has(candidate));
      if (id === undefined) continue; // pinned key isn't a rendered board node
      const m = byId.get(id)!;
      // Same sizing + zero-fallback the solver applies to free nodes
      // (`n.w > 0 ? n.w : NODE_W`) — a pinned rect's w/h are used VERBATIM
      // as the node's effective size, so the fallback must be baked here.
      const { w, h } = resolveNodeSize(extractWidgetSpec(m), m);
      merged.set(id, { x: pos.x, y: pos.y, w: w > 0 ? w : NODE_W, h: h > 0 ? h : NODE_H });
    }
    for (const [id, r] of placements) merged.set(id, r); // user placements win
    return merged;
  }, [placements, viewPlan, layoutInputs]);

  // Placement-independent topology (LCA edge routing, external ports, internal
  // calls, parent index) for the WHOLE model. Patched incrementally from the
  // prior build per the module/edge diff (full rebuild only on a structural —
  // add/remove/reparent/rename/edge — change). Shared by both the global
  // connection index AND the board engine. Drag-stable: independent of
  // placements/drag state, so dragging reuses it.
  const structure = useMemo<LayoutStructure | null>(
    () => (layoutInputs ? structureBuilderRef.current!.update(layoutInputs.flat, layoutInputs.effEdges) : null),
    [layoutInputs],
  );

  // Global edge/port aggregation for the connection index + selected-edge
  // lookup. Just layers an empty placement set onto the shared `structure` — no
  // second topology build.
  const globalCtx = useMemo(
    () =>
      layoutInputs && structure
        ? prepareLayout(
            layoutInputs.flat,
            layoutInputs.effEdges,
            undefined,
            undefined,
            false,
            undefined,
            undefined,
            structure,
          ).ctx
        : null,
    [layoutInputs, structure],
  );

  const root = useMemo(() => {
    if (!model || !layoutInputs) return null;
    const { flat, effEdges } = layoutInputs;
    const cacheKey = computeLayoutInputKey(
      flat,
      effEdges,
      effectivePlacements,
      isDragging,
      animating,
      solveGen,
      layoutInputs.boundaryPorts,
      layoutInputs.groupFrames,
    );
    const cached = lastRootRef.current;
    // Diagnostic counter exposed on window for live debugging; bumps
    // on every cache hit/miss so we can confirm in the running webview
    // whether the layout cache is doing its job during edits.
    const w = (typeof window !== "undefined" ? (window as unknown as Record<string, unknown>) : null);
    if (cached && cached.key === cacheKey) {
      if (w) w.__archCacheHits = ((w.__archCacheHits as number) ?? 0) + 1;
      return cached.root;
    }
    if (w) {
      w.__archCacheMisses = ((w.__archCacheMisses as number) ?? 0) + 1;
      w.__archLastKey = cacheKey;
      w.__archPrevKey = cached?.key;
    }
    // ONE board path: the lazy engine, for idle, drag, AND animation. The
    // engine renders only the focal subtree (focal + RENDER_DEPTH preview
    // levels) and reuses every unchanged space from the prior frame, so a drag
    // re-solves just the perturbed space — O(visible), not the whole tree. The
    // drag budget (`isDragging`) and the animation root override flow into the
    // ctx via `prepareLayout`; `placeSpace` reads them exactly as the old eager
    // build did, so per-frame output is identical, just lazily projected.
    const input: ModelInput = { modules: flat, edges: effEdges };
    const override = animating ? (animRootPositionsRef.current ?? undefined) : undefined;
    // Model-edit baseline: latched the moment the CONTENT (model/edges)
    // changes, from the last COMMITTED solved world (rects + the structure
    // they were solved under — a pair, kept consistent by the snapshot
    // effect). Latching, rather than deriving from the live refs at solve
    // time, is what keeps the pre-edit world available when a drag-regime
    // render runs first: that render advances the refs to the new content,
    // and a late derivation would diff the new content against itself —
    // a silent maintenance hold instead of an edit response.
    if (editContentRef.current !== layoutInputs) {
      const prevInputs = editContentRef.current as typeof layoutInputs | null;
      editContentRef.current = layoutInputs;
      editBaseRef.current =
        solvedStructureRef.current && previousLayoutRef.current.size > 0
          ? {
              rects: previousLayoutRef.current,
              structure: solvedStructureRef.current,
              // Pre-edit frame membership — the baseline world's partition.
              ...(prevInputs?.groupFrames
                ? { groupFrames: prevInputs.groupFrames }
                : {}),
            }
          : null;
      // A drag/animation render caches this content without the edit
      // response — the eventual idle consumer must invalidate (the engine
      // signature is blind to editBase, so the drag-cached spaces would
      // otherwise be carried verbatim and the response never runs). True
      // whether the edit lands ON a drag render (this branch) or a drag
      // render follows before the idle one (branch below).
      editStaleRef.current = isDragging || animating;
    } else if ((isDragging || animating) && editBaseRef.current) {
      editStaleRef.current = true;
    }
    // Warm starts are only valid for roughly the SAME graph. While the model
    // streams in at startup (or a bulk edit lands), the edge set jumps by
    // whole files — reusing the previous partial-graph layout degrades every
    // solve into an incremental patch on stale geometry and the organize
    // pass's crystallization whipsaws. Drop the warm start on a material
    // (>15%) edge-count change; single-edit deltas never trip this.
    if (!isDragging && !animating) {
      // View-plan change involving `group by` frames (either side): a
      // grouped board solves cold by design (flat emit keeps ids stable
      // while their coordinate meaning changes — a stale warm rect would
      // seed wrong geometry). The NON-grouped world is stashed on entry and
      // restored on exit, so leaving a grouped view never reshuffles the
      // board the user left behind. Non-grouped view switches keep the warm
      // world untouched (the view-transition carried-verbatim guarantee).
      let crossedFrames = false;
      if (warmViewPlanRef.current.plan !== viewPlan) {
        const hadFrames = warmViewPlanRef.current.hadFrames;
        const hasFrames = (layoutInputs.groupFrames?.length ?? 0) > 0;
        if (!hadFrames && hasFrames) {
          framelessWarmStashRef.current = previousLayoutRef.current;
        }
        if (hadFrames || hasFrames) {
          previousLayoutRef.current =
            hadFrames && !hasFrames
              ? (framelessWarmStashRef.current ?? new Map())
              : new Map();
          editBaseRef.current = null;
          warmEdgeCountRef.current = null;
          crossedFrames = true;
        }
        warmViewPlanRef.current = { plan: viewPlan, hadFrames: hasFrames };
      }
      // Spaces solved lazily since the last commit (viewport dives) live
      // only in the engine — fold them into the warm start before this
      // solve reads it, or an edit inside a freshly-dived space cold-
      // reshuffles the very space the user is looking at. NOT when this
      // render crossed a frame boundary: the outgoing engine's snapshot is
      // the OTHER world's geometry (flat emit keeps ids identical), and
      // merging it clobbers the deliberate stash-restore / cold reset
      // (bugloop r1 — grouped detour reshuffled the default board).
      if (engineRef.current && !crossedFrames) {
        const snap = engineRef.current.snapshot();
        if (snap.size > 0) {
          const merged = new Map(previousLayoutRef.current);
          for (const [k, v] of snap) merged.set(k, v);
          previousLayoutRef.current = merged;
        }
      }
      const edgeCount = effEdges.length;
      const prevCount = warmEdgeCountRef.current;
      if (prevCount !== null && shouldDropWarmStart(prevCount, edgeCount)) {
        previousLayoutRef.current = new Map();
      }
      warmEdgeCountRef.current = edgeCount;
      dragBaseRef.current = null; // gesture over — next drag re-baselines
    } else if (isDragging && !dragBaseRef.current) {
      // Gesture start: freeze the standing layout as the trim baseline.
      dragBaseRef.current = new Map(previousLayoutRef.current);
    }
    // The latched baseline is consumed by the FIRST idle solve of the new
    // content; placeSpace diffs each space against it and treats real
    // geometric deltas as edit gestures (a value-only recompute matches the
    // baseline and keeps its maintenance hold). Later idle re-runs of the
    // same content — placements, settle ticks — are not edits.
    const editBase =
      !isDragging && !animating ? (editBaseRef.current ?? undefined) : undefined;
    if (!isDragging && !animating) editBaseRef.current = null;
    // Drag-throttle parity step (see dragFrameParityRef). Identity-keyed on
    // effectivePlacements: exactly one advance per drag frame.
    const dragThrottleSkip = (): boolean => {
      if (dragParityPlacementsRef.current !== effectivePlacements) {
        dragParityPlacementsRef.current = effectivePlacements;
        dragFrameParityRef.current = (dragFrameParityRef.current + 1) % 2;
      }
      return dragFrameParityRef.current === 1;
    };
    if (!isDragging) {
      // Parity 1 so the first frame of the NEXT drag advances to 0 = SOLVE
      // (immediate neighbour response), then alternates.
      dragFrameParityRef.current = 1;
      dragParityPlacementsRef.current = null;
    }
    // Reuse the shared `structure` (already built this render) so the engine
    // skips its own O(N+E) topology pass — only the cheap placement layer is
    // rebuilt, carrying the current pins, warm-start, drag budget, and animation
    // override into the ctx the engine's per-space signature + solver read.
    const prepared = structure
      ? prepareLayout(
          flat,
          effEdges,
          effectivePlacements,
          previousLayoutRef.current,
          isDragging,
          override,
          undefined,
          structure,
          undefined,
          isDragging ? (dragBaseRef.current ?? undefined) : undefined,
          editBase,
          layoutInputs.groupFrames,
          layoutInputs.boundaryPorts,
          isDragging && readDragThrottleSwitch() && dragThrottleSkip(),
        )
      : undefined;
    const engine = createBoardEngine(flat, effEdges, {
      placements: effectivePlacements,
      previousLayout: previousLayoutRef.current,
      previous: engineRef.current ?? undefined,
      prepared,
    });
    // A stale edit baseline (its content was already engine-cached by a
    // drag/animation render) needs a full bust: unchanged spaces re-place
    // as cheap maintenance holds, changed ones run the edit response the
    // drag render swallowed. Happens at most once per edit.
    if (editBase && editStaleRef.current && structure) {
      engine.invalidate([
        "root::space",
        ...[...structure.byParent.keys()].map((k) => `${k}::space`),
      ]);
      editStaleRef.current = false;
    }
    // Invalidate only what the engine's per-space signature can't see itself.
    // That signature already busts on any same-level content/size/edge/pin
    // change, so a pure value edit (or a drag, where the moved pin changes the
    // dragged space's signature) needs NO explicit invalidation. The one gap is
    // edge-derived, cross-level state (external ports, internal calls,
    // aggregation counts) — which can only change when an edge changed OR the
    // symbol surface changed. So we run the (rebuild-free) `diffModel` only when
    // the compiler reports a structural edit OR the raw-edge signature moved.
    const edgeSig = edgesSignature(effEdges);
    if (engineRef.current && prevModelInputRef.current) {
      if (structural || edgeSig !== prevEdgeSigRef.current) {
        engine.invalidate(
          diffModel(prevModelInputRef.current, input, {
            prevStructure: prevStructureRef.current,
            nextStructure: structure ?? undefined,
          }).dirtySpaces,
        );
      }
    }
    // The animation override drives the root positions but isn't part of any
    // space signature, so the engine can't see it change frame-to-frame — bust
    // the root each animation frame so it re-projects the new positions.
    if (animating) engine.invalidate(["root::space"]);
    engineRef.current = engine;
    prevModelInputRef.current = input;
    prevStructureRef.current = structure;
    prevEdgeSigRef.current = edgeSig;
    const next: SpaceSource = engineSource(engine, RENDER_DEPTH);
    lastRootRef.current = { key: cacheKey, root: next };
    return next;
    // solveGen forces re-runs from the Recompute / Animate buttons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, layoutInputs, structure, structural, effectivePlacements, isDragging, solveGen, animating]);

  // Resolve a module id to the focal path (space ids) of its CONTAINING space,
  // walking the model's parent chain. Model-derived so flyTo reaches modules
  // below the lazy board's solved preview depth.
  const moduleFocalPath = useCallback(
    (moduleId: string): readonly string[] | null => {
      if (!layoutInputs) return null;
      const byKey = new Map(layoutInputs.flat.map((m) => [moduleKey(m), m] as const));
      if (!byKey.has(moduleId)) return null;
      const chain: string[] = [];
      let pid = (byKey.get(moduleId) as { parentId?: string }).parentId;
      while (pid) {
        chain.unshift(`${pid}::space`);
        pid = (byKey.get(pid) as { parentId?: string } | undefined)?.parentId;
      }
      return ["root::space", ...chain];
    },
    [layoutInputs],
  );

  const recomputeLayout = useCallback(() => {
    // Drop the warm-start cache so the solver does a full cold solve (or
    // an incremental one if there are still pinned placements), and exit
    // drag mode so the full budget runs instead of the drag budget.
    previousLayoutRef.current = new Map();
    editBaseRef.current = null; // fresh solve — pending edit gestures moot
    setIsDragging(false);
    setAnimating(false);
    setSolveGen((g) => g + 1);
  }, []);

  const animateRecompute = useCallback(async () => {
    if (!model || !layoutInputs) return;
    // Abort any prior animation by bumping the token.
    const myToken = ++animAbortRef.current;

    previousLayoutRef.current = new Map();
    editBaseRef.current = null; // fresh solve — pending edit gestures moot
    setIsDragging(false);

    // Build the exact same StressInput the sync solver would use for the
    // root space, then drive `stressLayoutSteps` externally with a sleep
    // between each yield. The generator is the single source of truth
    // for the solver schedule — animation and sync share it. The inputs are
    // the SAME `layoutInputs` the sync path solves — projection (active view)
    // and aspect overlay included — so Animate under a view relaxes the
    // projected subset only, never the raw full model (anim/sync equality).
    // Grouped view → the same forced partition the sync solve uses (frame
    // groups); omitting it here would break animation/sync equality.
    const animPartition = layoutInputs.groupFrames?.length
      ? new Map(
          layoutInputs.groupFrames.flatMap((f) =>
            f.memberBoardIds.map((id) => [id, f.id] as const),
          ),
        )
      : undefined;
    const animParents = layoutInputs.groupFrames?.length
      ? new Map(
          layoutInputs.groupFrames.flatMap((f) =>
            f.parentId !== undefined ? [[f.id, f.parentId] as const] : [],
          ),
        )
      : undefined;
    const rootInput = prepareRootStressInput(
      layoutInputs.flat,
      layoutInputs.effEdges,
      effectivePlacements,
      new Map(),
      undefined,
      animPartition,
      animParents?.size ? animParents : undefined,
    );
    const gen = stressLayoutSteps(rootInput);

    // Build a fresh gate for this animation so Pause/Step can block the
    // loop between frames.
    const gate = (() => {
      let open = true;
      const waiters: Array<() => void> = [];
      return {
        wait: (): Promise<void> =>
          open ? Promise.resolve() : new Promise<void>((r) => waiters.push(r)),
        pause: () => {
          open = false;
        },
        resume: () => {
          open = true;
          while (waiters.length) waiters.shift()!();
        },
        step: () => {
          // Let exactly one waiter through; gate stays closed.
          const w = waiters.shift();
          if (w) w();
        },
      };
    })();
    animGateRef.current = gate;
    setAnimPaused(false);
    setAnimating(true);

    // Kept in step with LR: LR was scaled 1/3, so FRAME_MS is 1/3 (more
    // visible steps per second at smaller delta), and teleport dwell
    // multiplier is 3× to preserve the same visible hold time on each
    // discrete jump.
    const FRAME_MS = 9;
    const TELEPORT_FRAMES = 120;
    const ALIGN_DWELL_FRAMES = 60;
    const lastTypeRef = { current: null as string | null };
    const sleep = (ms: number) =>
      new Promise<void>((r) => setTimeout(r, ms));

    let step = gen.next();
    while (!step.done) {
      if (animAbortRef.current !== myToken) return;
      // Publish the current positions to the React tree.
      animRootPositionsRef.current = step.value.result.positions;
      setSolveGen((g) => g + 1);
      const isTeleport = step.value.kind === "placeAtIdeal";
      // First "align" step after a non-align step — dwell briefly so the
      // viewer registers the phase boundary instead of mistaking the
      // tidy pass for trailing settle iters.
      const isFirstAlign =
        step.value.kind === "align" && lastTypeRef.current !== "align";
      lastTypeRef.current = step.value.kind;
      // Ghost-repair mover — drives a yellow dashed outline on the
      // travelling node so the user can watch its journey through the
      // crowd. Cleared on every non-ghost step kind (iter, sgd, separate,
      // align, placeAtIdeal) so other phases render normally.
      const nextMover =
        step.value.kind === "ghost" || step.value.kind === "ghostSettle"
          ? (step.value.moverId ?? null)
          : null;
      if (nextMover !== ghostMoverIdRef.current) {
        ghostMoverIdRef.current = nextMover;
        setGhostMoverId(nextMover);
      }
      const dwell = isTeleport
        ? TELEPORT_FRAMES
        : isFirstAlign
          ? ALIGN_DWELL_FRAMES
          : 1;
      await sleep(FRAME_MS * dwell);
      if (animAbortRef.current !== myToken) return;
      // Block here while paused; resume or step releases the wait.
      await gate.wait();
      step = gen.next();
    }

    // Final state — clear the override so buildBoardFromModules runs a
    // real (incremental) solve on the settled positions. End result
    // equals what a sync solve would have produced.
    animRootPositionsRef.current = null;
    animGateRef.current = null;
    ghostMoverIdRef.current = null;
    setGhostMoverId(null);
    setAnimPaused(false);
    setAnimating(false);
    setSolveGen((g) => g + 1);
  }, [model, layoutInputs, effectivePlacements]);

  const toggleAnimPause = useCallback(() => {
    const g = animGateRef.current;
    if (!g) return;
    if (animPaused) {
      g.resume();
      setAnimPaused(false);
    } else {
      g.pause();
      setAnimPaused(true);
    }
  }, [animPaused]);

  const animStep = useCallback(() => {
    const g = animGateRef.current;
    if (!g) return;
    g.pause();
    setAnimPaused(true);
    g.step();
  }, []);

  // Placement seam — a hand-placement flips to drag mode so the light drag
  // budget holds while the user is actively repositioning.
  const onPlaced = useCallback(() => {
    setIsDragging(true);
  }, []);

  // Placement seam — "Reset layout" also drops the warm-start cache, forcing a
  // cold solve so the graph finds a fresh global optimum instead of relaxing
  // around stale positions.
  const resetLayout = useCallback(() => {
    previousLayoutRef.current = new Map();
    editBaseRef.current = null; // fresh solve — pending edit gestures moot
    setIsDragging(false);
  }, []);

  // Warm-start snapshot: carry the engine's solved (visible) spaces forward so
  // the next frame's solve nudges neighbours from their last positions rather
  // than reshuffling. The engine is the only board path now, so this is always
  // an engine snapshot — drives both edit warm-starts and the per-drag-frame
  // gentle drift.
  useEffect(() => {
    if (!root || !engineRef.current) return;
    const snap = engineRef.current.snapshot();
    // MERGE, never replace-with-less: an unmounted board (editor-only
    // layout) means nothing consumed the engine this commit — its empty
    // snapshot must not wipe the standing warm start. Rects of nodes no
    // longer in the model are kept deliberately: they are the removal
    // baselines for edit gestures (placeSpace filters per space anyway).
    if (snap.size === 0) return;
    const merged = new Map(previousLayoutRef.current);
    for (const [k, v] of snap) merged.set(k, v);
    previousLayoutRef.current = merged;
    solvedStructureRef.current = prevStructureRef.current;
  }, [root]);

  // Expose `window.arch` debug helpers whenever we have a current root.
  useEffect(() => {
    if (!root) return;
    return installWindowDebug(asSource(root).at([]));
  }, [root]);

  return {
    root,
    globalCtx,
    moduleFocalPath,
    ghostMoverId,
    animating,
    animPaused,
    recomputeLayout,
    animateRecompute,
    toggleAnimPause,
    animStep,
    onPlaced,
    resetLayout,
    viewPlan,
    viewNodeIds: layoutInputs?.viewIndex ?? null,
  };
}
